import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import Textfield from '@atlaskit/textfield';
import SearchIcon from '@atlaskit/icon/core/search';
import VisuallyHidden from '@atlaskit/visually-hidden';

const i18n = defineMessages({
	searchPlaceholder: {
		id: 'performance-insights.content-metrics.view-controls.search-placeholder.non-final',
		defaultMessage: 'Search by title',
		description:
			'Placeholder text for the search field above the content metrics table. Allows admins to filter the table by content title. Displayed on the Content metrics tab of the Performance analytics page.',
	},
	searchIconLabel: {
		id: 'performance-insights.content-metrics.view-controls.search-icon-label.non-final',
		defaultMessage: 'Search',
		description:
			'Accessible label for the search icon in the content metrics search field. Displayed on the Content metrics tab of the Performance analytics page.',
	},
	searchDescription: {
		id: 'performance-insights.content-metrics.view-controls.search-description.non-final',
		defaultMessage: 'Content will be filtered as you type',
		description:
			'Screen reader description for the search field. Informs assistive technology users that the table results update live as they type. Displayed on the Content metrics tab of the Performance analytics page.',
	},
});

const styles = cssMap({
	searchIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

const SEARCH_DESCRIPTION_ID = 'content-metrics-search-description';

export type ViewControlsProps = {
	searchText: string;
	onSearchChange: (value: string) => void;
};

export const ViewControls = ({ searchText, onSearchChange }: ViewControlsProps) => {
	const { formatMessage } = useIntl();

	const handleChange = (e: React.FormEvent<HTMLInputElement>) => {
		onSearchChange(e.currentTarget.value);
	};

	return (
		<>
			<Textfield
				isCompact
				width="medium"
				aria-label={formatMessage(i18n.searchPlaceholder)}
				aria-describedby={SEARCH_DESCRIPTION_ID}
				placeholder={formatMessage(i18n.searchPlaceholder)}
				value={searchText}
				onChange={handleChange}
				elemBeforeInput={
					<span aria-hidden="true" css={styles.searchIcon}>
						<SearchIcon spacing="spacious" label={formatMessage(i18n.searchIconLabel)} />
					</span>
				}
			/>
			<VisuallyHidden id={SEARCH_DESCRIPTION_ID}>
				{formatMessage(i18n.searchDescription)}
			</VisuallyHidden>
		</>
	);
};
